import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, catchError, map, Observable, of, tap } from 'rxjs';
import { Employee } from './employee.model';
import { environment } from '../../../environments/environment';
/**
 * Loads the internal employee list (`GET /phoneservice/v3/employees`) and
 * caches it for the lifetime of the user session. The TAN dialog, and
 * potentially other future consumers, reuse the cached array.
 */
@Injectable({ providedIn: 'root' })
export class EmployeeApiService {
  private readonly http = inject(HttpClient);
  // Session-long cache: one fetch per app session, shared across callers. No TTL.
  // activeOnly is applied post-cache so the cached superset is reused for both modes.
  private readonly cache$ = new BehaviorSubject<Employee[] | null>(null);

  /**
   * Fetch employees. Returns the cached list if present, otherwise hits the API.
   *
   * @param opts.activeOnly When `true` (default), terminated employees are filtered out.
   */
  getEmployees(opts?: { activeOnly?: boolean }): Observable<Employee[]> {
    const activeOnly = opts?.activeOnly ?? true;
    const cached = this.cache$.value;
    if (cached) {
      return of(this.filter(cached, activeOnly));
    }
    return this.http.get<Employee[]>(`${environment.phoneServiceUrl}/employees`).pipe(
      tap(list => this.cache$.next(list)),
      map(list => this.filter(list, activeOnly)),
    );
  }

  /** Clear the cached employee list. Intended for tests. */
  clearCache(): void {
    this.cache$.next(null);
  }

  /**
   * Server-side typeahead via `GET /employees?search=…`. Use this for autocomplete inputs —
   * the cached `getEmployees()` returns the full list and is meant for select dropdowns.
   *
   * The backend rejects 1–2 character queries with 400; callers should debounce and gate on
   * `query.trim().length >= 3` before invoking.
   */
  /**
   * Fetch a single employee by ID. Returns null on 404 / network failure so
   * callers can gracefully fall back to ID-only rendering. Unlike the bare
   * `/employees` list endpoint, this variant includes `firstName` / `lastName`.
   */
  findEmployeeById(id: number | string): Observable<Employee | null> {
    return this.http.get<Employee>(`${environment.phoneServiceUrl}/employees/${id}`).pipe(
      map((e) => e ?? null),
      // Any HTTP error -> null result; the caller's error branch never fires.
      catchError(() => of(null)),
    );
  }

  searchEmployees(query: string, opts?: { activeOnly?: boolean }): Observable<Employee[]> {
    const trimmed = query.trim();
    if (trimmed.length < 3) return of([]);
    const activeOnly = opts?.activeOnly ?? true;
    const params = new HttpParams().set('search', trimmed);
    return this.http.get<Employee[]>(`${environment.phoneServiceUrl}/employees`, { params }).pipe(
      map((list) => this.filter(list, activeOnly)),
    );
  }

  private filter(list: Employee[], activeOnly: boolean): Employee[] {
    return activeOnly ? list.filter(e => e.terminated == null) : list;
  }
}
